import { useEffect, useMemo, useState } from "react";

import { PlusIcon, PrinterIcon, ClockIcon } from "@heroicons/react/24/outline";

import Card from "../../shared/Card/Card";
import PartImage from "../PartImage";
import RsCoreImage from "../../core/RsCoreImage";
import StockStatusPill from "./StockStatusPill";
import VehiclePartHistory from "../../vehicles/VehiclePartHistory";
import classNames from "../../../utils/classNames";
import openInNewTab from "../../../utils/openInNewTab";
import PriceFormat from "../../../utils/priceFormat";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useJobsByVehicle from "../../../data/useJobsByVehicle";
import useVehicleSingle from "../../../data/useVehicleSingle";
import { useLabelGenerator } from "../../../hooks/useLabelGenerator";
import { IPart, PartStatus } from "../../../models/Part";
import { IVehicle } from "../../../models/Vehicle";

type FilterId = "all" | "available";

interface StockVehiclePartsProps {
  vehicleId: string;
  currentPartId?: string;
}

export default function StockVehicleParts({
  vehicleId,
  currentPartId,
}: StockVehiclePartsProps) {
  const { vehicle: singleVehicle } = useVehicleSingle(vehicleId);
  const { jobs, createWithPart } = useJobsByVehicle(vehicleId);
  const { saveHandlers } = useDefaultCRUDHandlers("Job");
  const { generatePartLabel } = useLabelGenerator();

  const [vehicle, setVehicle] = useState<IVehicle | undefined>(undefined);
  const [selectedPart, setSelectedPart] = useState<IPart | null>(null);
  const [filter, setFilter] = useState<FilterId>("all");

  useEffect(() => {
    if (!singleVehicle.isLoading && singleVehicle.data?.data) {
      setVehicle(singleVehicle.data.data);
    }
  }, [singleVehicle.isLoading, singleVehicle.data]);

  const allParts = useMemo<IPart[]>(
    () => (vehicle?.parts ?? []).filter((p): p is IPart => !!p),
    [vehicle?.parts]
  );

  const availableParts = useMemo<IPart[]>(() => {
    if (jobs.isLoading || !jobs.data) return [];
    const jobPartCategoryIds = new Set<string>();
    jobs.data.data.forEach((job) => {
      job.jobPartCategories.forEach((jp) => {
        if (jp.partCategoryId) jobPartCategoryIds.add(jp.partCategoryId);
      });
    });
    return allParts.filter(
      (p) =>
        p.partStatus === PartStatus["On The Car"] &&
        p.categoryId &&
        !jobPartCategoryIds.has(p.categoryId)
    );
  }, [jobs.isLoading, jobs.data, allParts]);

  const stats = useMemo(() => {
    const total = allParts.length;
    const sold = allParts.filter((p) => p.partStatus === PartStatus.Sold).length;
    const dismantled = allParts.filter(
      (p) => p.partStatus === PartStatus.Dismantled
    ).length;
    const onCar = allParts.filter(
      (p) => p.partStatus === PartStatus["On The Car"]
    ).length;
    const qc = allParts.filter((p) => p.partStatus === PartStatus.QC).length;
    return { total, sold, dismantled, onCar, qc };
  }, [allParts]);

  const displayed = filter === "all" ? allParts : availableParts;

  const handleCreateJob = (part: IPart) => {
    const currentJobId =
      jobs.data?.data && jobs.data.data.length > 0 ? jobs.data.data[0].id : null;
    createWithPart.mutate({ partId: part.id, jobId: currentJobId }, saveHandlers);
  };

  const handlePrint = async (part: IPart | null) => {
    if (!part) return;
    part.vehicle = vehicle ?? null;
    const html = await generatePartLabel(part);
    const iframe = document.getElementById("stock-parts-print-frame") as HTMLIFrameElement | null;
    const pri = iframe?.contentWindow;
    pri?.document.open();
    pri?.document.write(html);
    pri?.document.close();
    pri?.focus();
    setTimeout(() => pri?.print(), 1);
  };

  if (singleVehicle.isLoading) {
    return (
      <Card bodyClassName="p-6 text-sm text-gray-500">Loading parts…</Card>
    );
  }

  if (!vehicle) {
    return (
      <Card bodyClassName="p-6 text-sm text-gray-500">Vehicle not found.</Card>
    );
  }

  if (allParts.length === 0) {
    return (
      <Card bodyClassName="p-6 text-sm text-gray-500">
        No parts recorded for this vehicle.
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <VehiclePartHistory
        part={selectedPart}
        open={!!selectedPart}
        onClose={setSelectedPart}
      />

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatTile label="Total" value={stats.total} />
        <StatTile label="On The Car" value={stats.onCar} />
        <StatTile label="Dismantled" value={stats.dismantled} />
        <StatTile label="QC" value={stats.qc} />
        <StatTile label="Sold" value={stats.sold} />
      </div>

      {/* Filter + count */}
      <div className="flex items-center justify-between gap-4">
        <div className="inline-flex rounded-md bg-gray-100 p-1 text-sm">
          <FilterToggle
            label="All Parts"
            count={allParts.length}
            active={filter === "all"}
            onClick={() => setFilter("all")}
          />
          <FilterToggle
            label="Available for Job"
            count={availableParts.length}
            active={filter === "available"}
            onClick={() => setFilter("available")}
          />
        </div>
        <p className="text-xs text-gray-500">
          {displayed.length} showing
        </p>
      </div>

      {/* Table */}
      {displayed.length === 0 ? (
        <Card bodyClassName="p-6 text-sm text-gray-500">
          {filter === "available"
            ? "No available parts found. All parts are either already in jobs or have been removed from the vehicle."
            : "No parts match this filter."}
        </Card>
      ) : (
        <Card bodyClassName="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th className="w-20">Image</Th>
                <Th>Part</Th>
                <Th className="w-32">Tag</Th>
                <Th className="w-28">Price</Th>
                <Th className="w-40">Status</Th>
                <Th className="w-40 text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {displayed.map((part) => {
                const isCurrent = currentPartId && part.id === currentPartId;
                return (
                  <tr
                    key={part.id}
                    onClick={() => openInNewTab(`/stock/${part.id}`)}
                    className={classNames(
                      "cursor-pointer hover:bg-gray-50 transition-colors",
                      isCurrent ? "bg-primary-50/60" : ""
                    )}
                  >
                    <td className="py-2 pl-4">
                      <div className="w-16 h-16 flex items-center justify-center">
                        <PartImage part={part} width="max-w-16" />
                      </div>
                    </td>
                    <td className="py-2 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{part.title ?? "-"}</span>
                        {isCurrent && (
                          <span className="inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                            Viewing
                          </span>
                        )}
                      </div>
                      {part.category?.name && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {part.category.name}
                        </div>
                      )}
                    </td>
                    <td className="py-2 text-sm text-gray-700 font-mono">
                      {part.tagNumber ?? "-"}
                    </td>
                    <td className="py-2 text-sm text-gray-900">
                      {PriceFormat(part.price ?? part.enteredPrice ?? 0)}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <StockStatusPill part={part} />
                        {part.isCore && <RsCoreImage />}
                      </div>
                    </td>
                    <td
                      className="py-2 pr-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1">
                        <IconButton
                          title="Print Label"
                          onClick={() => handlePrint(part)}
                        >
                          <PrinterIcon className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                          title="View History"
                          onClick={() => setSelectedPart(part)}
                        >
                          <ClockIcon className="h-4 w-4" />
                        </IconButton>
                        {filter === "available" && (
                          <IconButton
                            title="Create Job"
                            onClick={() => handleCreateJob(part)}
                            primary
                            disabled={createWithPart.isLoading}
                          >
                            <PlusIcon className="h-4 w-4" />
                          </IconButton>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <iframe id="stock-parts-print-frame" className="h-0 w-0 absolute" title="Print" />
    </div>
  );
}

const StatTile = ({ label, value }: { label: string; value: number }) => (
  <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </p>
    <p className="mt-0.5 text-xl font-semibold text-gray-900">{value}</p>
  </div>
);

const FilterToggle = ({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={classNames(
      "inline-flex items-center gap-2 rounded px-3 py-1.5 font-medium transition-colors",
      active
        ? "bg-white text-gray-900 shadow-sm"
        : "text-gray-500 hover:text-gray-700"
    )}
  >
    {label}
    <span
      className={classNames(
        "inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs",
        active ? "bg-primary-100 text-primary-700" : "bg-gray-200 text-gray-600"
      )}
    >
      {count}
    </span>
  </button>
);

const Th = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <th
    scope="col"
    className={classNames(
      "py-2 pl-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500",
      className
    )}
  >
    {children}
  </th>
);

const IconButton = ({
  children,
  onClick,
  title,
  primary,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  primary?: boolean;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    disabled={disabled}
    className={classNames(
      "inline-flex items-center justify-center rounded-md h-8 w-8 transition-colors",
      disabled ? "opacity-50 cursor-not-allowed" : "",
      primary
        ? "bg-primary-600 text-white hover:bg-primary-700"
        : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
    )}
  >
    {children}
  </button>
);
